/*
  CA89 snow tracker: shared data loader for the homepage strip and the Snow Report page.

  Sources (both free, no key, browser-readable):
  - NRCS SNOTEL hourly sensors: snow depth (SNWD, inches) and air temperature (TOBS, F).
    Station times are Pacific Standard Time all year.
  - National Weather Service gridpoints: forecast snowfall (mm) and the text forecast.

  New snow is worked out from depth readings. Raw hourly depth wobbles by an inch or more
  (Truckee read 1-0-0-0-1 on a dry morning), so readings are smoothed with a 9-hour rolling
  median. "New snow" over a window = latest smoothed depth minus the lowest smoothed depth
  inside that window, and it only counts if the snow's water content (SWE, WTEQ) also rose
  at least 0.1" in that window. Wobble moves depth, real snowfall moves both. If a station
  has no SWE readings for the window, the depth figure stands alone.
*/
(function () {
  if (window.CA89Snow) return;

  var STATIONS = [
    { id: '834:CA:SNTL', key: 'truckee', name: 'Truckee', elev: 6500, grid: 'REV/30,98' },
    { id: '809:CA:SNTL', key: 'tahoe-city', name: 'Tahoe City', elev: 6760, grid: 'REV/30,93' },
    { id: '428:CA:SNTL', key: 'donner', name: 'Donner Summit', elev: 6890, grid: 'STO/86,94' },
    { id: '784:CA:SNTL', key: 'palisades', name: 'Palisades Tahoe', elev: 8010, grid: 'STO/89,87' }
  ];
  var CACHE_KEY = 'ca89-snow-v3';
  var CACHE_MS = 20 * 60 * 1000;
  var HISTORY_DAYS = 15;

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  // SNOTEL wants dates in station time (PST, UTC-8, no daylight saving).
  function pstDate(msAgo) {
    var d = new Date(Date.now() - msAgo - 8 * 3600 * 1000);
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
  }

  // "2026-11-02 16:00" in PST -> epoch ms
  function pstToMs(s) {
    var p = s.split(/[- :]/);
    return Date.UTC(+p[0], +p[1] - 1, +p[2], +p[3] + 8, +p[4]);
  }

  function median(arr) {
    var a = arr.slice().sort(function (x, y) { return x - y; });
    var m = Math.floor(a.length / 2);
    return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
  }

  function smooth(points) {
    return points.map(function (p, i) {
      var win = points.slice(Math.max(0, i - 8), i + 1).map(function (q) { return q.v; });
      return { t: p.t, v: median(win) };
    });
  }

  // Rise from the lowest point in the window to the latest reading.
  function rise(sm, from) {
    if (!sm.length) return null;
    var last = sm[sm.length - 1], low = Infinity;
    sm.forEach(function (p) { if (p.t >= from && p.v < low) low = p.v; });
    return low === Infinity ? null : last.v - low;
  }

  function newSnow(sm, swe, hours) {
    if (!sm.length) return null;
    var from = sm[sm.length - 1].t - hours * 3600 * 1000;
    var d = rise(sm, from);
    if (d == null) return null;
    var hasSwe = swe.some(function (p) { return p.t >= from; });
    if (hasSwe && rise(swe, from) < 0.1) return 0;
    return Math.max(0, Math.round(d));
  }

  // One value per day (PST): the smoothed depth at the last reading of that day.
  function daily(sm) {
    var days = {};
    sm.forEach(function (p) {
      var d = new Date(p.t - 8 * 3600 * 1000);
      var k = d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
      days[k] = Math.max(0, Math.round(p.v));
    });
    return Object.keys(days).sort().map(function (k) { return { day: k, depth: days[k] }; });
  }

  function loadSnotel() {
    var url = 'https://wcc.sc.egov.usda.gov/awdbRestApi/services/v1/data?stationTriplets=' +
      STATIONS.map(function (s) { return s.id; }).join(',') +
      '&elements=SNWD,WTEQ,TOBS&duration=HOURLY&beginDate=' + pstDate(HISTORY_DAYS * 86400000) +
      '&endDate=' + pstDate(-86400000);
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('SNOTEL ' + r.status);
      return r.json();
    });
  }

  // Fraction of an NWS interval ("2026-11-03T18:00:00+00:00/PT6H") that falls between now and end.
  function overlap(validTime, now, end) {
    var parts = validTime.split('/');
    var start = Date.parse(parts[0]);
    var m = /P(?:(\d+)D)?T?(?:(\d+)H)?/.exec(parts[1]) || [];
    var len = ((+m[1] || 0) * 24 + (+m[2] || 0)) * 3600 * 1000 || 3600 * 1000;
    var a = Math.max(start, now), b = Math.min(start + len, end);
    return b > a ? (b - a) / len : 0;
  }

  // Forecast over the next `hours`: snowfall in inches, plus how sure NWS is that snow falls at all.
  // NWS often forecasts snow with no measurable accumulation (e.g. "Snow likely", 0 mm), so the
  // weather-type grid is read too. Slight chance and isolated are ignored.
  var LIKELY = ['likely', 'definite', 'numerous', 'occasional', 'periods', 'frequent', 'widespread'];
  function forecastSnow(grid, hours) {
    var p = (grid || {}).properties || {};
    var now = Date.now(), end = now + hours * 3600 * 1000, mm = 0, chance = null;
    ((p.snowfallAmount || {}).values || []).forEach(function (v) {
      var f = overlap(v.validTime, now, end);
      if (f && v.value) mm += v.value * f;
    });
    ((p.weather || {}).values || []).forEach(function (v) {
      if (!overlap(v.validTime, now, end)) return;
      (v.value || []).forEach(function (w) {
        if (!w || !/snow/.test(w.weather || '')) return;
        if (LIKELY.indexOf(w.coverage) > -1) chance = 'likely';
        else if (w.coverage === 'chance' && chance !== 'likely') chance = 'chance';
      });
    });
    return { inches: mm / 25.4, chance: chance };
  }

  function loadNws(path) {
    return fetch('https://api.weather.gov/gridpoints/' + path, { headers: { Accept: 'application/geo+json' } })
      .then(function (r) { if (!r.ok) throw new Error('NWS ' + r.status); return r.json(); });
  }

  function loadNwsText(path) {
    return fetch('https://api.weather.gov/gridpoints/' + path + '/forecast', { headers: { Accept: 'application/geo+json' } })
      .then(function (r) { if (!r.ok) throw new Error('NWS ' + r.status); return r.json(); })
      .then(function (j) {
        return ((j.properties || {}).periods || []).slice(0, 6).map(function (p) {
          return { name: p.name, temp: p.temperature, short: p.shortForecast, detail: p.detailedForecast, day: p.isDaytime };
        });
      });
  }

  function build(snotel, grids, texts) {
    var byId = {};
    (snotel || []).forEach(function (st) { byId[st.stationTriplet] = st; });
    var updated = 0;
    var stations = STATIONS.map(function (s, i) {
      var out = { key: s.key, name: s.name, elev: s.elev, ok: false };
      var st = byId[s.id];
      if (st) {
        var depth = [], swe = [], temp = null;
        st.data.forEach(function (el) {
          var code = el.stationElement.elementCode;
          var pts = el.values.filter(function (x) { return x.value != null && x.value > -1; })
            .map(function (x) { return { t: pstToMs(x.date), v: x.value }; });
          if (code === 'SNWD') depth = pts;
          if (code === 'WTEQ') swe = smooth(pts);
          if (code === 'TOBS' && pts.length) temp = pts[pts.length - 1].v;
        });
        if (depth.length) {
          var sm = smooth(depth);
          var last = sm[sm.length - 1];
          out.ok = true;
          out.depth = Math.max(0, Math.round(last.v));
          out.new24 = newSnow(sm, swe, 24);
          out.new48 = newSnow(sm, swe, 48);
          out.new7d = newSnow(sm, swe, 168);
          out.daily = daily(sm);
          out.asOf = last.t;
          if (last.t > updated) updated = last.t;
        }
        out.temp = temp == null ? null : Math.round(temp);
      }
      out.forecast48 = grids[i] ? forecastSnow(grids[i], 48) : null;
      out.periods = texts[i] || null;
      return out;
    });
    return { stations: stations, updated: updated, fetched: Date.now() };
  }

  function readCache(withText) {
    try {
      var c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (c && Date.now() - c.fetched < CACHE_MS && (!withText || c.withText)) return c;
    } catch (e) {}
    return null;
  }

  function writeCache(d) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(d)); } catch (e) {}
  }

  function settle(p) { return p.then(function (v) { return v; }, function () { return null; }); }

  // load({ withText: true }) also pulls the NWS text forecast (used on the full page).
  function load(opts) {
    var withText = !!(opts && opts.withText);
    var cached = readCache(withText);
    if (cached) return Promise.resolve(cached);
    return Promise.all([
      settle(loadSnotel()),
      Promise.all(STATIONS.map(function (s) { return settle(loadNws(s.grid)); })),
      withText ? Promise.all(STATIONS.map(function (s) { return settle(loadNwsText(s.grid)); })) : Promise.resolve([])
    ]).then(function (r) {
      var d = build(r[0], r[1], r[2]);
      d.withText = withText;
      if (d.stations.some(function (s) { return s.ok; })) writeCache(d);
      return d;
    });
  }

  function fmtForecast(f) {
    if (!f) return null;
    if (f.inches >= 0.5) return 'About ' + Math.max(1, Math.round(f.inches)) + '"';
    if (f.chance === 'likely') return 'Light snow, under 1"';
    if (f.chance === 'chance') return 'Chance of light snow';
    return 'None expected';
  }

  function fmtUpdated(ms) {
    if (!ms) return '';
    try {
      return new Date(ms).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', hour: 'numeric', minute: '2-digit' });
    } catch (e) { return ''; }
  }

  /* ---------- The fun part: falling snow and drifts sized to the real depth ---------- */

  var reduceMotion = false;
  try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  // One plain line per station, from the real depth.
  function mood(depth) {
    if (depth == null) return '';
    if (depth < 1) return 'Bare ground, for now';
    if (depth < 6) return 'A dusting';
    if (depth < 18) return 'Getting there';
    if (depth < 48) return 'Good coverage';
    if (depth < 96) return 'Deep winter';
    return 'Buried';
  }

  // How hard it snows on screen: heavier when snow just fell or is on the way.
  function intensity(d) {
    var level = 0.35;
    (d.stations || []).forEach(function (s) {
      var f = s.forecast48 || {};
      if (f.chance === 'chance') level = Math.max(level, 0.55);
      if (f.chance === 'likely' || f.inches >= 0.5) level = Math.max(level, 0.75);
      if (s.new24 >= 1) level = Math.max(level, 1);
    });
    return level;
  }

  // Falling snow on a canvas that fills `el` (el needs position: relative).
  function snowfall(el, level) {
    if (!el || el.querySelector('canvas.ca89-snowfall')) return;
    var c = document.createElement('canvas');
    c.className = 'ca89-snowfall';
    c.setAttribute('aria-hidden', 'true');
    c.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:1';
    el.insertBefore(c, el.firstChild);
    var ctx = c.getContext('2d'), dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0, h = 0, flakes = [], running = false, visible = true, last = 0;

    function make(randomY) {
      var r = 0.8 + Math.random() * 2.4;
      return {
        x: Math.random() * w, y: randomY ? Math.random() * h : -6,
        r: r, vy: 14 + r * 9 + Math.random() * 8, sway: 8 + Math.random() * 18,
        phase: Math.random() * 6.28, spin: Math.random() * 6.28, a: 0.55 + Math.random() * 0.4,
        star: r > 2.5
      };
    }
    function size() {
      var b = el.getBoundingClientRect();
      w = b.width; h = b.height;
      c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var n = Math.round(Math.min(160, (w * h) / 9000) * level);
      flakes = []; for (var i = 0; i < n; i++) flakes.push(make(true));
    }
    function drawFlake(f) {
      ctx.globalAlpha = f.y > h - 24 ? f.a * Math.max(0, (h - f.y) / 24) : f.a;
      if (!f.star) { ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, 6.283); ctx.fill(); return; }
      ctx.save(); ctx.translate(f.x, f.y); ctx.rotate(f.spin); ctx.beginPath();
      for (var k = 0; k < 3; k++) { ctx.rotate(1.047); ctx.moveTo(-f.r * 1.4, 0); ctx.lineTo(f.r * 1.4, 0); }
      ctx.stroke(); ctx.restore();
    }
    function frame(t) {
      if (!running) return;
      var dt = Math.min(0.05, last ? (t - last) / 1000 : 0.016); last = t;
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#fff'; ctx.strokeStyle = '#fff'; ctx.lineWidth = 1; ctx.lineCap = 'round';
      flakes.forEach(function (f, i) {
        f.y += f.vy * dt; f.phase += dt * 0.9; f.spin += dt * 0.4;
        f.x += Math.sin(f.phase) * f.sway * dt;
        if (f.y > h + 4) flakes[i] = make(false);
        drawFlake(f);
      });
      requestAnimationFrame(frame);
    }
    function start() { if (!running && visible && !document.hidden && !reduceMotion) { running = true; last = 0; requestAnimationFrame(frame); } }
    function stop() { running = false; }

    size();
    if (reduceMotion) { // still flakes, no motion
      ctx.fillStyle = '#fff'; ctx.strokeStyle = '#fff'; flakes.forEach(drawFlake); return;
    }
    var rt; window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(size, 200); });
    document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { visible = e[0].isIntersecting; visible ? start() : stop(); }).observe(el);
    }
    start();
  }

  // A snow drift along the bottom of `el`, its height following the real depth (inches).
  // Log scale so a 1" dusting still reads as a little pile and a 100" winter doesn't swallow the card.
  // At the 60px desktop cap: bare 4px, 1" 18px, 5" 31px, 24" 49px, 100"+ 60px.
  function drift(el, depth, maxPx) {
    if (!el || depth == null) return;
    var max = maxPx || 60, k = max / 60;
    var px = depth < 0.5 ? 4 * k : Math.min(max, (10 + 12 * Math.log(1 + depth)) * k);
    var seed = 0; for (var i = 0; i < (el.textContent || '').length; i++) seed += el.textContent.charCodeAt(i);
    var pts = [], n = 7;
    for (var j = 0; j <= n; j++) {
      var bump = Math.sin(seed + j * 1.7) * 0.18 + Math.sin(seed * 0.3 + j * 3.1) * 0.08;
      pts.push([j * (100 / n), 100 * (0.22 + bump)]);
    }
    var d = 'M0,100 L0,' + pts[0][1].toFixed(1);
    for (var k = 1; k < pts.length; k++) {
      var p0 = pts[k - 1], p1 = pts[k], mx = (p0[0] + p1[0]) / 2;
      d += ' Q' + p0[0].toFixed(1) + ',' + p0[1].toFixed(1) + ' ' + mx.toFixed(1) + ',' + ((p0[1] + p1[1]) / 2).toFixed(1);
    }
    d += ' T100,' + pts[n][1].toFixed(1) + ' L100,100 Z';
    var wrap = document.createElement('div');
    wrap.className = 'ca89-drift';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.cssText = 'position:absolute;left:0;right:0;bottom:0;height:' + px.toFixed(0) + 'px;pointer-events:none;z-index:2;' +
      'transform-origin:bottom;transform:scaleY(' + (reduceMotion ? 1 : 0) + ');transition:transform 1.4s cubic-bezier(.2,.8,.2,1)';
    // Inline size: the theme's global svg rules otherwise make this as tall as it is wide.
    wrap.innerHTML = '<svg viewBox="0 0 100 100" preserveAspectRatio="none" style="display:block;width:100%;height:100%;max-width:none">' +
      '<path d="' + d + '" fill="#fff"/><path d="' + d + '" fill="none" stroke="rgba(120,150,180,.35)" stroke-width="1.2" vector-effect="non-scaling-stroke"/></svg>';
    el.appendChild(wrap);
    if (!reduceMotion) requestAnimationFrame(function () { requestAnimationFrame(function () { wrap.style.transform = 'scaleY(1)'; }); });
    return px;
  }

  window.CA89Snow = {
    load: load, fmtForecast: fmtForecast, fmtUpdated: fmtUpdated, stations: STATIONS,
    mood: mood, intensity: intensity, snowfall: snowfall, drift: drift
  };
})();
